import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import type {
    BoardCardShortcut,
    BoardCardShortcutTarget,
} from "@design-system/Board/Board.types";
import {
    BOARD_CARD_ATTRIBUTE,
    findItemCard,
} from "@design-system/Board/boardAttributes";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    TASK_ACTION_LABELS,
    type TaskBoardChange,
    type TaskBoardField,
} from "./taskBoardActions";
import {
    describeTaskUpdate,
    type TaskChangeOutcome,
} from "./taskBoardChangeRules";
import { findTaskFieldChip } from "@components/tasks/board/TaskChipButton";

/** A field a shortcut edits through a picker; R renames in place instead. */
export type TaskPickerField = Exclude<TaskBoardField, "title">;

const SHORTCUTS: ReadonlyArray<{ field: TaskBoardField; key: string }> = [
    { field: "assignee", key: "a" },
    { field: "status", key: "s" },
    { field: "priority", key: "p" },
    { field: "dueDate", key: "d" },
    { field: "entities", key: "e" },
    { field: "title", key: "r" },
];

/** A picker a card shortcut opened. */
export interface TaskShortcutPick {
    field: TaskPickerField;
    target: BoardCardShortcutTarget;
    /** Where it opens: the field's chip on the focused card, else the card. */
    anchorRect: DOMRect;
}

// An entity pick replaces the task's list, so it reaches the focused card
// alone: several tasks have no one list to start from.
const createPick = (
    field: TaskPickerField,
    target: BoardCardShortcutTarget,
): TaskShortcutPick => ({
    field,
    target:
        field === "entities"
            ? { ...target, itemIds: new Set([target.itemId]) }
            : target,
    anchorRect: (
        findTaskFieldChip(target.card, field) ?? target.card
    ).getBoundingClientRect(),
});

interface UseTaskBoardShortcutsArgs {
    updateTasks: (
        taskIds: ReadonlySet<string>,
        change: TaskBoardChange,
    ) => TaskChangeOutcome;
    /** Whether the change filters any of these tasks off the board. */
    isHiddenByFilters: (
        taskIds: ReadonlySet<string>,
        change: TaskBoardChange,
    ) => boolean;
    /** Fields that edit from the card; only these get a shortcut. */
    editableFields: ReadonlySet<TaskBoardField>;
}

/**
 * The board's card shortcuts, for editable fields only: A, S, P and D open
 * the Assign, Status, Priority and Due date pickers for the focused card, or
 * for the selection when it holds the card. E opens the Entity picker and R
 * renames (through the card's pencil), both for the focused card alone. A
 * pick on several tasks toasts like the command bar; on one it doesn't, like
 * a chip, unless the task couldn't change or the change filters it off the
 * board. Closing hands focus back to the card, or to its new card when the
 * pick moved it to another column or lane. When the pick took it off the
 * board (into a collapsed column, or out of the filter), focus goes to the
 * board's tabbable card, else its first card.
 */
export const useTaskBoardShortcuts = ({
    updateTasks,
    isHiddenByFilters,
    editableFields,
}: UseTaskBoardShortcutsArgs) => {
    const [pick, setPick] = useState<TaskShortcutPick | null>(null);
    const cardShortcuts = useMemo(
        (): readonly BoardCardShortcut[] =>
            SHORTCUTS.filter(({ field }) => editableFields.has(field)).map(
                ({ field, key }) => ({
                    key,
                    label: TASK_ACTION_LABELS[field],
                    onTrigger: (target) =>
                        field === "title"
                            ? findTaskFieldChip(target.card, field)?.click()
                            : setPick(createPick(field, target)),
                }),
            ),
        [editableFields],
    );
    const { toast } = useToaster();
    const apply = useCallback(
        (taskIds: ReadonlySet<string>, change: TaskBoardChange) => {
            const outcome = updateTasks(taskIds, change);
            if (
                taskIds.size > 1 ||
                outcome.refusalReasons.length > 0 ||
                isHiddenByFilters(outcome.updatedIds, change)
            ) {
                toast(describeTaskUpdate(change, outcome));
            }
        },
        [updateTasks, isHiddenByFilters, toast],
    );
    const closedTargetRef = useRef<BoardCardShortcutTarget | null>(null);
    const close = useCallback(
        (card: HTMLElement) => {
            closedTargetRef.current = pick?.target ?? null;
            setPick(null);
            card.focus({ preventScroll: true });
        },
        [pick],
    );
    useLayoutEffect(() => {
        const target = closedTargetRef.current;
        closedTargetRef.current = null;
        if (target !== null && !target.card.isConnected) {
            const card =
                findItemCard(target.board, target.itemId) ??
                target.board.querySelector<HTMLElement>(
                    `[${BOARD_CARD_ATTRIBUTE}][tabindex="0"]`,
                ) ??
                target.board.querySelector<HTMLElement>(
                    `[${BOARD_CARD_ATTRIBUTE}]`,
                );
            card?.focus({ preventScroll: true });
        }
    }, [pick]);
    return { cardShortcuts, pick, apply, close };
};
